import cookie from './cookie.js'

/**
 * Feature detects if localStorage is available and working.
 *
 * @returns {boolean}
 */
const isLocalStorageAvailable = () => {
  try {
    const testKey = '__storage_test__'

    window.localStorage.setItem(testKey, testKey)
    window.localStorage.removeItem(testKey)

    return true
  } catch {
    return false
  }
}

/**
 * Modern Cookie Storage Fallback
 *
 * Keys are prefixed to avoid colliding with server-side authentication cookies.
 */
class CookieStorageFallback {
  prefix = 'app_storage_'

  getItem(key) {
    return cookie.get(`${this.prefix}${key}`)
  }

  setItem(key, value) {
    cookie.set(`${this.prefix}${key}`, value, { sameSite: 'Strict', maxAge: 60 * 60 * 24 * 365 })
  }

  removeItem(key) {
    cookie.remove(`${this.prefix}${key}`, { sameSite: 'Strict' })
  }

  clear() {
    cookie
      .names()
      .filter((name) => name.startsWith(this.prefix))
      .forEach((name) => cookie.remove(name, { sameSite: 'Strict' }))
  }
}

// Select the best available storage engine at runtime
const storageEngine = isLocalStorageAvailable() ? window.localStorage : new CookieStorageFallback()

/**
 * Modern, safe storage wrapper with automatic JSON transformations.
 */
export default {
  /**
   * Retrieve an item from storage.
   *
   * @param {string} key
   * @param {boolean} [parseJson=false] - Automatically parse JSON if true.
   * @returns {*|null}
   */
  getItem(key, parseJson = false) {
    try {
      const value = storageEngine.getItem(key)

      if (value === null) {
        return null
      }

      return parseJson ? JSON.parse(value) : value
    } catch {
      return null
    }
  },

  /**
   * Save an item to storage.
   *
   * @param {string} key
   * @param {*} value - Primitive values or Objects/Arrays.
   * @returns {boolean} True if successful.
   */
  setItem(key, value) {
    try {
      const stringValue = typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value)

      storageEngine.setItem(key, stringValue)

      return true
    } catch {
      return false
    }
  },

  /**
   * Delete an item from storage.
   *
   * @param {string} key
   * @returns {boolean} True if successful.
   */
  removeItem(key) {
    try {
      storageEngine.removeItem(key)

      return true
    } catch {
      return false
    }
  },

  /**
   * Wipe all keys managed by this utility.
   *
   * @returns {boolean} True if successful.
   */
  clear() {
    try {
      storageEngine.clear()

      return true
    } catch {
      return false
    }
  },
}
